import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { associatedDocumentEditionService } from "../services/associatedDocumentEditionService";
import { associatedDocumentEditionDisplayId } from "./AssociatedDocumentEditionCommonStore";
import { AssociatedDocumentEdition } from "../../../api/graphql";
import { editionDocumentTypeDisplayId } from "../../editionDocumentType/store/EditionDocumentTypeCommonStore";
import {
  downloadWithAuth,
  fetchFileBlob,
} from "../../../common/utils/downloadWithAuth";
import { CrudRows } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import { editionDocumentTypeService } from "../../editionDocumentType/services/editionDocumentTypeService";
import { InitializeParams } from "../../../store/types";

export class AssociatedDocumentEditionViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = associatedDocumentEditionService;
  associatedDocumentEditionInstance: AssociatedDocumentEdition | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  nameValue: string = "";
  fileFiles: CrudRows = {};
  typeService = editionDocumentTypeService;
  typeSearchValue: string = "";
  typeIdValue: string = "";
  typeSelectedValue: { displayId: string; id: string } | null = null;
  notesValue: string = "";
  publisherConfigurationDisplayValue: string = "";
  authorsDisplayValue: string = "";
  pdfViewerOpen: boolean = false;
  pdfViewerUrl: string = "";
  pdfViewerTitle: string = "";

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadAssociatedDocumentEditionById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.associatedDocumentEditionOne({ id });
    this.associatedDocumentEditionInstance =
      result.associatedDocumentEditionOne;
    if (this.associatedDocumentEditionInstance) {
      this.loadValues(this.associatedDocumentEditionInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.associatedDocumentEditionInstance) {
      return associatedDocumentEditionDisplayId(
        this.associatedDocumentEditionInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.AssociatedDocumentEditionSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "type") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.editionDocumentTypeOne({
          id: params.returnData.resultId,
        });
        const editionDocumentType = result.editionDocumentTypeOne;
        this.typeSelectedValue = editionDocumentType;
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  get typeValue() {
    if (this.typeSelectedValue) {
      return {
        id: this.typeSelectedValue.id,
        displayId: editionDocumentTypeDisplayId(
          this.typeSelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  *fileDownload(props: any, fieldName: string): any {
    yield downloadWithAuth({
      entityName: "AssociatedDocumentEdition",
      fieldName: fieldName,
      instanceId: this.associatedDocumentEditionInstance?.id!,
      fileName: props.name,
    });
  }

  openPdfViewer(url: string, title: string) {
    this.pdfViewerUrl = url;
    this.pdfViewerTitle = title;
    this.pdfViewerOpen = true;
  }

  closePdfViewer() {
    if (this.pdfViewerUrl) {
      window.URL.revokeObjectURL(this.pdfViewerUrl);
    }
    this.pdfViewerOpen = false;
    this.pdfViewerUrl = "";
    this.pdfViewerTitle = "";
  }

  *viewFilePdf(props: any, fieldName: string): any {
    try {
      const blob = yield fetchFileBlob({
        entityName: "AssociatedDocumentEdition",
        fieldName: fieldName,
        instanceId: this.associatedDocumentEditionInstance?.id!,
        fileName: props.name,
      });
      if (!blob) return;
      this.openPdfViewer(window.URL.createObjectURL(blob), props.name);
    } catch (error) {
      console.error("Error loading PDF:", error);
    }
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.associatedDocumentEditionInstance = null;
    this.nameValue = "";
    this.fileFiles = {};
    this.typeSearchValue = "";
    this.typeIdValue = "";
    this.notesValue = "";
    this.publisherConfigurationDisplayValue = "";
    this.authorsDisplayValue = "";
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelAssociatedDocumentEdition() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return false;
  }

  editAssociatedDocumentEdition() {
    const id = this.associatedDocumentEditionInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/associatedDocumentEdition/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printAssociatedDocumentEdition(handlePrint: () => void): void {
    handlePrint();
  }

  *closeAssociatedDocumentEdition(): any {
    yield this.close();
  }

  loadValues(instance: AssociatedDocumentEdition) {
    this.idValue = instance.id;
    this.nameValue = instance.name;
    this.fileFiles = instance.file_data
      ? {
          [instance.file_data.name]: {
            name: instance.file_data.name,
            formattedSize: formatBytes(instance.file_data?.size || 0),
            mimetype: instance.file_data?.mimetype,
            id: instance.file_data.name,
            __status__: "original",
          },
        }
      : {};
    this.typeSelectedValue = instance.type ? instance.type : null;
    this.notesValue = instance.notes || "";
    this.publisherConfigurationDisplayValue =
      instance.publisherConfigurationDisplay || "";
    this.authorsDisplayValue = instance.authorsDisplay || "";
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadAssociatedDocumentEditionById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
